import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from '@/components/ui/command';
import { FileText, MessageSquare, TrendingUp, Users, Clock, LayoutDashboard, Settings, BarChart3, ScrollText, CreditCard, Bell, Shield, Activity, Server, ListTodo, Columns3, FolderKanban } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcons = {
  report: FileText,
  complaint: MessageSquare,
  deal: TrendingUp,
  team: Users,
  attendance: Clock,
  task: ListTodo,
};

const typeLabels = {
  report: 'Reports',
  complaint: 'Complaints',
  deal: 'Deals',
  team: 'Team',
  attendance: 'Attendance',
  task: 'Tasks',
};

const quickActions = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'Submit Report', icon: FileText, path: '/dashboard/reports/submit' },
  { label: 'All Reports', icon: FileText, path: '/dashboard/reports' },
  { label: 'Complaints', icon: MessageSquare, path: '/dashboard/complaints' },
  { label: 'Sales Pipeline', icon: TrendingUp, path: '/dashboard/sales/pipeline' },
  { label: 'Check In', icon: Clock, path: '/dashboard/attendance/checkin' },
  { label: 'Leave Management', icon: Clock, path: '/dashboard/attendance/leaves' },
  { label: 'Team', icon: Users, path: '/dashboard/team' },
  { label: 'Activity Feed', icon: Activity, path: '/dashboard/activity' },
  { label: 'Analytics', icon: BarChart3, path: '/dashboard/analytics' },
  { label: 'Audit Logs', icon: ScrollText, path: '/dashboard/audit-logs' },
  { label: 'Security', icon: Shield, path: '/dashboard/security' },
  { label: 'Billing', icon: CreditCard, path: '/dashboard/billing' },
  { label: 'Notifications', icon: Bell, path: '/dashboard/notifications' },
  { label: 'My Tasks', icon: ListTodo, path: '/dashboard/tasks/my' },
  { label: 'Kanban Board', icon: Columns3, path: '/dashboard/tasks/kanban' },
  { label: 'Projects', icon: FolderKanban, path: '/dashboard/tasks/projects' },
  { label: 'System Status', icon: Server, path: '/dashboard/status' },
  { label: 'Settings', icon: Settings, path: '/dashboard/settings' },
];

export function GlobalSearch({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setResults(res.data.results || []);
    } catch { setResults([]); }
    setSearching(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (query) search(query); else setResults([]); }, 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); }
  }, [open]);

  const handleSelect = (path) => {
    onOpenChange(false);
    navigate(path);
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 max-w-lg" aria-describedby={undefined}>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <CommandInput
            placeholder="Search reports, complaints, deals, team..."
            value={query}
            onValueChange={setQuery}
            data-testid="global-search-input"
          />
          <CommandList>
            {!query && !results.length && (
              <CommandGroup heading="Quick Navigation">
                {quickActions.map(a => (
                  <CommandItem key={a.path} onSelect={() => handleSelect(a.path)} data-testid={`search-nav-${a.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <a.icon className="w-4 h-4 mr-2 text-slate-500" />
                    <span>{a.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {query && !searching && results.length === 0 && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}

            {searching && (
              <div className="py-6 text-center text-sm text-slate-500">Searching...</div>
            )}

            {Object.entries(grouped).map(([type, items]) => {
              const Icon = typeIcons[type] || FileText;
              return (
                <CommandGroup key={type} heading={typeLabels[type] || type}>
                  {items.map(item => (
                    <CommandItem key={`${type}-${item.id}`} value={`${type}-${item.id}`} onSelect={() => handleSelect(item.path)} data-testid={`search-result-${type}-${item.id}`}>
                      <Icon className="w-4 h-4 mr-2 text-slate-500 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium truncate">{item.title}</span>
                        <span className="text-xs text-slate-500 truncate">{item.subtitle}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
